import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  // Tabs,
  // Tab,
  List,
  ListItem,
  // ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import captureScreenshot from 'utils/screenshotUtils';
import pptxgen from 'pptxgenjs';
import { motion } from 'framer-motion';
import CompaniesMarketTrends from './CompaniesMarketTrends';
import CompaniesPricingAnalysis from './CompaniesPricingAnalysis';
import FieldInsightsCard from './FieldInsightsCard';
// import FieldAnalysisMenu from './FieldAnalysisMenu';
import ReportSecondaryMenu from './ReportSecondaryMenu';
import Favicon from 'components/Favicon';
import { useDispatch, useSelector } from 'react-redux';
import useAntiTamperBlur from 'hooks/useAntiTamperBlur';
import { openModal } from 'store/reducers/actions';

// Field categories and their mappings to data fields
const FIELD_CATEGORIES = {
  'Side-by-Side Analysis': {
    sectionId: 'section-sbs-analysis',
    fields: [
      { name: 'Overview', id: 'section-sbs-overview' },
      { name: 'Competitive Index', id: 'section-competitive-index' },
      { name: 'Feature Matrix', id: 'section-feature-matrix' },
      { name: 'Heatmap Scorecard', id: 'section-heatmap-scorecard' },
      { name: 'Positioning Matrices', id: 'section-positioning-matrices' }
    ]
  },
  'USP & Distinctions': {
    dataPath: 'analysis_data.usp',
    fields: ['USP', 'DISTINCTION']
  },
  Overview: {
    dataPath: 'analysis_data.overview_result.result.0',
    fields: [
      'Main Value Proposition',
      'USP',
      'Competitive advantages',
      'Strengths',
      'Differentiation',
      'CTA',
      'Pricing',
      'Audience',
      'Industry',
      'Use Cases',
      'Description',
      'Positioning',
      'Key Features',
      'Social Proof',
      'Tone of Voice',
      'Trust Factors',
      'Brand Positioning'
    ]
  },
  'Market Trends': {
    dataPath: 'analysis_data.market_trends',
    fields: ['trends', 'benchmarks']
  },
  'Pricing Analysis': {
    dataPath: 'analysis_data.pricing_analysis',
    fields: ['pricing_model', 'pricing_strategy', 'pricing_conclusion']
  }
};

// // Get nested property from object safely
// const getNestedProperty = (obj, path) => {
//   if (!obj) return null;

//   const properties = path.split('.');
//   let current = obj;

//   for (const property of properties) {
//     if (current === null || current === undefined) {
//       return null;
//     }
//     current = current[property];
//   }

//   return current;
// };

// New component for Overview tabs view with integrated field insights
const OverviewTabsView = ({ competitors, companyData, isPaywalled, handleTrialClick, trialAllowed }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [showAll, setShowAll] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const contentRef = useRef(null);
  const GateField = ({ children, cta }) => {
    const contentBlurRef = useRef(null);
    const overlayBlurRef = useRef(null);
    useAntiTamperBlur(contentBlurRef, overlayBlurRef, { blurPx: 5 });
    if (!isPaywalled) return children;
    return (
      <Box sx={{ position: 'relative' }}>
        <Box ref={contentBlurRef} sx={{ filter: 'blur(4px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.85 }}>
          {children}
        </Box>
        <Paper
          ref={overlayBlurRef}
          elevation={3}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 3,
            backdropFilter: 'blur(3px)',
            backgroundColor: 'rgba(255,255,255,0.6)'
          }}
        >
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 6,
                p: { xs: 2, md: 3 },
                width: '92%',
                maxWidth: 720,
                textAlign: 'left'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {cta?.title || 'Unlock full report'}
              </Typography>
              {Array.isArray(cta?.bullets) && cta.bullets.length > 0 && (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {cta.bullets.map((b, i) => (
                    <Grid key={i} item xs={12} sm={6}>
                      <Typography variant="body2">• {b}</Typography>
                    </Grid>
                  ))}
                </Grid>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleTrialClick}
                  component={motion.button}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{ px: 3, py: 1.25, background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)', boxShadow: 'none' }}
                >
                  {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Cancel anytime
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Paper>
      </Box>
    );
  };

  const getFieldCta = (field) => {
    const label = typeof field === 'string' ? field : 'Overview Field';
    const std = { title: `Unlock ${label}`, bullets: ['My company vs competitors', 'Insights & recommendations'] };
    switch (field) {
      case 'USP':
        return { title: 'Unlock USP & Differentiators', bullets: ['Strengths & weaknesses', 'Competitor highlights', 'Recommendations'] };
      case 'Pricing':
        return { title: 'Unlock Pricing Field', bullets: ['Model & strategy', 'Conclusions'] };
      case 'Audience':
        return { title: 'Unlock Audience Field', bullets: ['Target segments', 'Messaging alignment'] };
      case 'Positioning':
        return { title: 'Unlock Positioning Field', bullets: ['Market position', 'Differentiation'] };
      case 'Key Features':
        return { title: 'Unlock Key Features', bullets: ['Capabilities by company', 'Gaps & opportunities'] };
      case 'Social Proof':
        return { title: 'Unlock Social Proof', bullets: ['Testimonials & numbers', 'Credibility signals'] };
      case 'Trust Factors':
        return { title: 'Unlock Trust Factors', bullets: ['Safety & privacy', 'Proof points'] };
      default:
        return std;
    }
  };

  // Get a color for each competitor
  const getCompetitorColor = (index) => {
    const colors = [
      '#3f51b5', // primary
      '#f44336', // red
      '#4caf50', // green
      '#ff9800', // orange
      '#2196f3', // blue
      '#9c27b0', // purple
      '#00bcd4', // cyan
      '#ff5722', // deep orange
      '#673ab7', // deep purple
      '#795548' // brown
    ];
    return colors[index % colors.length];
  };

  // Get fields for Overview
  const { fields } = FIELD_CATEGORIES['Overview'];

  // Check if company data has overview information
  const hasCompanyOverview = companyData && companyData.overview_result && companyData.overview_result.length > 0;

  // Get the main company name - use service_name if available, then company_name, then url, but never 'My Company'
  const mainCompanyName =
    companyData?.overview_result?.find((item) => Object.keys(item)[0] === 'Service Name')?.['Service Name'] ||
    companyData?.company_name ||
    companyData?.service_name ||
    companyData?.url ||
    '';

  const handleTabChange = (newValue) => {
    setCurrentTab(newValue);
    setShowAll(false);
  };

  const handleShowAll = () => {
    setShowAll(true);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Helper function to get field value from overview_result array or analysis.field_analysis
  const getFieldValue = (data, fieldName) => {
    if (!data) return null;

    // Check overview_result first
    if (data.overview_result) {
      const field = data.overview_result.find((item) => Object.keys(item)[0] === fieldName);
      if (field) return field[fieldName];
    }

    // Check analysis.field_analysis
    if (data.analysis?.field_analysis?.[fieldName.toLowerCase()]) {
      const fieldData = data.analysis.field_analysis[fieldName.toLowerCase()];
      if (fieldData.my_company?.description) {
        return fieldData.my_company.description;
      }
    }

    return null;
  };

  // Render a content row with vertical line
  const renderContentWithLine = (content, color, fieldName) => {
    // Special rendering for USP field
    if (fieldName === 'USP' && companyData?.analysis?.field_analysis?.usp?.my_company) {
      const usp = companyData.analysis.field_analysis.usp.my_company;
      return (
        <Box>
          {usp.strengths && usp.strengths.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
                Strengths:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {usp.strengths.map((item, idx) => (
                  <li key={idx} style={{ color: '#388e3c' }}>
                    {item}
                  </li>
                ))}
              </ul>
            </Box>
          )}
          {usp.weaknesses && usp.weaknesses.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'error.main' }}>
                Weaknesses:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {usp.weaknesses.map((item, idx) => (
                  <li key={idx} style={{ color: '#d32f2f' }}>
                    {item}
                  </li>
                ))}
              </ul>
            </Box>
          )}
          {usp.description && (
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Description:
              </Typography>
              <Typography variant="body2" sx={{ pl: 1 }}>
                {usp.description}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }
    // Default rendering
    if (!content)
      return (
        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
          N/A
        </Typography>
      );
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box
          sx={{
            minWidth: '3px',
            width: '3px',
            alignSelf: 'stretch',
            backgroundColor: color,
            mr: 1,
            borderRadius: '1px'
          }}
        />
        <Typography variant="body1">{content}</Typography>
      </Box>
    );
  };

  // Export as PPTX function
  const downloadAsPPTX = async () => {
    // Create a new presentation
    const pptx = new pptxgen();

    // Get fields to export
    const fieldsToExport = showAll ? fields : [fields[currentTab]];

    // Track overall slide count
    let totalSlideCount = 0;
    let slideCount = 0;

    // Estimate text row count for PowerPoint
    const estimateRowCount = (text) => {
      if (!text) return 1;

      // Convert to string if not already
      const str = typeof text === 'string' ? text : JSON.stringify(text) || '';

      // Approximate characters per line in PPT (varies based on font size, this is an estimate)
      const charsPerLine = 80;

      // Count newlines and approximate line wraps
      const newlineCount = (str.match(/\n/g) || []).length;
      const lineWrapCount = Math.floor(str.length / charsPerLine);

      return Math.max(1, newlineCount + lineWrapCount + 1);
    };

    // Determine max rows per slide based on content length
    const getMaxRowsPerSlide = (rowData) => {
      // Get the content from the second column (field data)
      const content = rowData[1];
      const rowCount = estimateRowCount(content);

      if (rowCount <= 1) return 10; // Short text - up to 10 items per slide
      if (rowCount <= 2) return 7; // Medium text - up to 7 items per slide
      return 5; // Long text - up to 5 items per slide
    };

    // First, count total slides needed for pagination
    for (let fieldIndex = 0; fieldIndex < fieldsToExport.length; fieldIndex++) {
      const field = fieldsToExport[fieldIndex];

      // Prepare full table data for this field
      const fullTableData = [['Company', field]]; // Header row

      // Add company data if exists
      if (hasCompanyOverview && getFieldValue(companyData, field)) {
        fullTableData.push([mainCompanyName, getFieldValue(companyData, field)]);
      }

      // Add competitor data
      competitors.forEach((competitor) => {
        const content = getFieldValue(competitor, field);

        if (content) {
          fullTableData.push([getFieldValue(competitor, 'Service Name') || competitor.url, content]);
        }
      });

      // If no data for this field, skip
      if (fullTableData.length <= 1) continue;

      // Determine the number of content rows (excluding header)
      const contentRows = fullTableData.length - 1;

      // Calculate slides needed based on content length of each row
      let rowsProcessed = 0;
      while (rowsProcessed < contentRows) {
        // Look ahead to the next set of rows to determine how many will fit on a slide
        const rowsRemaining = contentRows - rowsProcessed;

        // Get the shortest text length rows to fill the current slide
        let rowsToProcess = 0;
        let currentSlideMaxRows = 5; // Default to 5

        // Get the next row data to evaluate (skip header, consider already processed rows)
        const nextRowIndex = rowsProcessed + 1; // +1 to account for header
        if (nextRowIndex < fullTableData.length) {
          currentSlideMaxRows = getMaxRowsPerSlide(fullTableData[nextRowIndex]);
        }

        // Take the minimum of what's available and what will fit
        rowsToProcess = Math.min(rowsRemaining, currentSlideMaxRows);

        // Count this slide
        totalSlideCount++;
        rowsProcessed += rowsToProcess;
      }
    }

    // Now create slides for each field
    for (let fieldIndex = 0; fieldIndex < fieldsToExport.length; fieldIndex++) {
      const field = fieldsToExport[fieldIndex];

      // Prepare full table data for this field
      const fullTableData = [['Company', field]]; // Header row

      // Add company data if exists
      if (hasCompanyOverview && getFieldValue(companyData, field)) {
        fullTableData.push([mainCompanyName, getFieldValue(companyData, field)]);
      }

      // Add competitor data
      competitors.forEach((competitor) => {
        const content = getFieldValue(competitor, field);

        if (content) {
          fullTableData.push([getFieldValue(competitor, 'Service Name') || competitor.url, content]);
        }
      });

      // If no data for this field, skip
      if (fullTableData.length <= 1) continue;

      // Process rows dynamically based on text length
      let rowsProcessed = 0;
      const contentRows = fullTableData.length - 1; // Exclude header

      while (rowsProcessed < contentRows) {
        // Create a new slide
        const slide = pptx.addSlide();
        slideCount++;

        // Set slide background
        slide.background = { color: 'FFFFFF' };

        // Add title
        slide.addText('Overview Comparison', {
          x: 0.5,
          y: 0.5,
          w: '90%',
          h: 1,
          fontSize: 24,
          color: '363636',
          align: 'center',
          bold: true
        });

        // Add subtitle with pagination
        const subtitle = `Competitive analysis - ${field} (${slideCount}/${totalSlideCount})`;

        slide.addText(subtitle, {
          x: 0.5,
          y: 1.2,
          w: '90%',
          h: 0.5,
          fontSize: 14,
          color: '666666',
          align: 'center',
          italic: true
        });

        // Add branding
        slide.addText('Powered by', {
          x: 7.0,
          y: 0.5,
          w: 1.1,
          h: 0.4,
          fontSize: 10,
          color: '666666',
          align: 'right',
          isTextBox: true
        });

        // Logo
        slide.addImage({
          path: '/favicon.svg',
          x: 8.15,
          y: 0.51,
          w: 0.3,
          h: 0.3
        });

        slide.addText('Askpot', {
          x: 8.5,
          y: 0.5,
          w: 1,
          h: 0.4,
          fontSize: 13,
          color: '363636',
          bold: true,
          align: 'left',
          isTextBox: true
        });

        // Get the next row data to evaluate (skip header, consider already processed rows)
        const nextRowIndex = rowsProcessed + 1; // +1 to account for header
        let maxRowsForCurrentSlide = 5; // Default

        if (nextRowIndex < fullTableData.length) {
          maxRowsForCurrentSlide = getMaxRowsPerSlide(fullTableData[nextRowIndex]);
        }

        // Calculate which rows to include in this slide
        const rowsRemaining = contentRows - rowsProcessed;
        const rowsToInclude = Math.min(rowsRemaining, maxRowsForCurrentSlide);

        // Create table data for this slide (header + content rows)
        const slideTableData = [
          fullTableData[0], // Header row
          ...fullTableData.slice(rowsProcessed + 1, rowsProcessed + rowsToInclude + 1) // Content rows
        ];

        // Add table to slide
        slide.addTable(slideTableData, {
          x: 0.5,
          y: 2,
          w: '90%',
          colW: [2.5, 6],
          border: { type: 'solid', color: 'CCCCCC', pt: 1 },
          fontSize: 11,
          color: '363636',
          align: 'left',
          valign: 'middle',
          headerRow: true,
          headerColor: 'F5F5F5',
          headerFontSize: 12,
          headerBold: true
        });

        // Add footer
        slide.addText('Generated by Askpot.com', {
          x: 0.5,
          y: '90%',
          w: '90%',
          h: 0.5,
          fontSize: 10,
          color: '999999',
          align: 'center'
        });

        // Update processed rows count
        rowsProcessed += rowsToInclude;
      }
    }

    // Save the presentation
    pptx.writeFile({ fileName: 'overview-comparison.pptx' });
    handleClose();
  };

  // Convert to RTF for DOC export
  const convertToRTF = () => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    // Escape RTF characters
    const escapeRTF = (text) => {
      if (typeof text !== 'string') {
        text = JSON.stringify(text) || '';
      }
      return text.replace(/\\/g, '\\\\').replace(/{/g, '\\{').replace(/}/g, '\\}');
    };

    // Add title
    rtf += '\\b\\fs28 Overview Comparison\\b0\\fs20\\par\\par\n';
    rtf += '\\i Competitive analysis\\i0\\par\\par\n';

    // Get fields to export
    const fieldsToExport = showAll ? fields : [fields[currentTab]];

    // Add section for each field
    fieldsToExport.forEach((field) => {
      rtf += '\\b\\fs24 ' + escapeRTF(field) + '\\b0\\fs20\\par\\par\n';

      // Table header
      rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
      rtf += '\\cellx3000\\cellx9000\n';
      rtf += '\\pard\\intbl\\b Company\\b0\\cell\\b ' + escapeRTF(field) + '\\b0\\cell\\row\n';

      // Company data
      if (hasCompanyOverview && getFieldValue(companyData, field)) {
        const companyName = mainCompanyName;
        const content = getFieldValue(companyData, field);

        rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
        rtf += '\\cellx3000\\cellx9000\n';
        rtf += '\\pard\\intbl ' + escapeRTF(companyName) + '\\cell ' + escapeRTF(content) + '\\cell\\row\n';
      }

      // Competitor data
      competitors.forEach((competitor) => {
        const content = getFieldValue(competitor, field);

        if (content) {
          rtf += '\\trowd\\trgaph108\\trleft0\\trqc\n';
          rtf += '\\cellx3000\\cellx9000\n';
          rtf +=
            '\\pard\\intbl ' +
            escapeRTF(getFieldValue(competitor, 'Service Name') || competitor.url) +
            '\\cell ' +
            escapeRTF(content) +
            '\\cell\\row\n';
        }
      });

      rtf += '\\par\\par\n';
    });

    rtf += '}';
    return rtf;
  };

  // Export as DOC
  const downloadAsDoc = () => {
    const rtfContent = convertToRTF();
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'overview-comparison.doc';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  // Export as PNG
  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'overview-comparison',
      afterCapture: handleClose
    });
  };

  // Render a single field table with insights card
  const renderFieldTable = (fieldIndex) => {
    const field = fields[fieldIndex];
    const fieldKeyForInsights = field.toLowerCase().replace(/ /g, '_');

    // Special case: render UspTableCard for USP field, with table first and insights card below
    if (field === 'USP') {
      const content = (
        <>
          <UspTableCard competitors={competitors} companyData={companyData} />
          <FieldInsightsCard fieldKey={fieldKeyForInsights} />
        </>
      );
      return (
        <Box sx={{ mb: showAll ? 4 : 0 }} key={field} id={`field-${field}`}>
          {showAll && (
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
              {field}
            </Typography>
          )}
          <GateField cta={getFieldCta(field)}>{content}</GateField>
        </Box>
      );
    }

    const table = (
      <>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{field}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasCompanyOverview && getFieldValue(companyData, field) && (
                <TableRow sx={{ bgcolor: 'primary.lighter' }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          mr: 1.5,
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}
                      >
                        ★
                      </Box>
                      <Favicon company={mainCompanyName} size={20} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.dark', ml: 1 }}>
                        {mainCompanyName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{renderContentWithLine(getFieldValue(companyData, field), 'primary.main', field)}</TableCell>
                </TableRow>
              )}
              {competitors.map((competitor, index) => {
                const content = getFieldValue(competitor, field);
                if (!content) return null;
                return (
                  <TableRow key={competitor.url} sx={{ '&:nth-of-type(odd)': { bgcolor: 'background.neutral' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: getCompetitorColor(index), borderRadius: '50%', mr: 1.5 }} />
                        <Favicon company={getFieldValue(competitor, 'Service Name') || competitor.url} size={20} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1 }}>
                          {getFieldValue(competitor, 'Service Name') || competitor.url}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{renderContentWithLine(content, getCompetitorColor(index), field)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <FieldInsightsCard fieldKey={fieldKeyForInsights} />
      </>
    );

    return (
      <Box sx={{ mb: showAll ? 4 : 0 }} key={field} id={`field-${field}`}>
        {showAll && (
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
            {field}
          </Typography>
        )}
        <GateField cta={getFieldCta(field)}>{table}</GateField>
      </Box>
    );
  };

  return (
    <Card sx={{ width: '100%', mb: 3, borderRadius: 2 }}>
      <CardContent ref={contentRef}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Fields Overview & Comparison
          </Typography>

          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              height: 28,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
            aria-label="download options"
            className="screenshot-controls"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            className="screenshot-controls"
            slotProps={{
              paper: {
                sx: {
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 1
                }
              }
            }}
          >
            <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
            <MenuItem onClick={downloadAsDoc}>Download as DOC</MenuItem>
            <MenuItem onClick={downloadAsPPTX}>Download as PPTX</MenuItem>
          </Menu>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Custom multi-row tabs with All button */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={1}>
            {/* All button */}
            <Grid item>
              <Box
                onClick={handleShowAll}
                data-field-index="all"
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: showAll ? 'primary.main' : 'background.neutral',
                  color: showAll ? 'white' : 'text.primary',
                  fontWeight: showAll ? 600 : 400,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: showAll ? 'primary.dark' : 'background.paper',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  },
                  border: 1,
                  borderColor: showAll ? 'primary.light' : 'divider',
                  whiteSpace: 'nowrap'
                }}
              >
                <Typography variant="body2">All Fields</Typography>
              </Box>
            </Grid>

            {/* Individual field buttons */}
            {fields.map((field, index) => (
              <Grid item key={field}>
                <Box
                  onClick={() => handleTabChange(index)}
                  data-field-index={index}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: !showAll && currentTab === index ? 'primary.main' : 'background.neutral',
                    color: !showAll && currentTab === index ? 'white' : 'text.primary',
                    fontWeight: !showAll && currentTab === index ? 600 : 400,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: !showAll && currentTab === index ? 'primary.dark' : 'background.paper',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    },
                    border: 1,
                    borderColor: !showAll && currentTab === index ? 'primary.light' : 'divider',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Typography variant="body2">{field}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Table content */}
        {showAll ? (
          // Show all tables stacked
          <Box>{fields.map((field, index) => renderFieldTable(index))}</Box>
        ) : (
          // Show single table for selected tab
          renderFieldTable(currentTab)
        )}
      </CardContent>
    </Card>
  );
};

// Special table-like component for USP & Distinctions
const UspTableCard = ({ competitors, companyData }) => {
  // Get a color for each competitor
  const getCompetitorColor = (index) => {
    const colors = [
      '#3f51b5', // primary
      '#f44336', // red
      '#4caf50', // green
      '#ff9800', // orange
      '#2196f3', // blue
      '#9c27b0', // purple
      '#00bcd4', // cyan
      '#ff5722', // deep orange
      '#673ab7', // deep purple
      '#795548' // brown
    ];
    return colors[index % colors.length];
  };

  // Get all USP and DISTINCTION items
  const fields = ['USP', 'DISTINCTION'];

  // Check if company data has USP information
  const hasCompanyUsp = companyData && companyData.usp;

  // Get the main company name - use service_name if available, then company_name, then url, but never 'My Company'
  // const mainCompanyName = companyData?.overview_result?.find(item =>
  //   Object.keys(item)[0] === 'Service Name'
  // )?.['Service Name'] || companyData?.company_name || companyData?.service_name || companyData?.url || '';

  return (
    <Card
      id="section-usp"
      sx={{
        height: '100%',
        borderRadius: 2,
        position: 'relative',
        isolation: 'isolate',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          padding: '1px',
          borderRadius: 'inherit',
          background: 'linear-gradient(135deg, rgba(63,81,181,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          zIndex: -1
        },
        boxShadow: '0 2px 4px rgba(0,0,0,0.002)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.003)'
        },
        bgcolor: 'background.paper'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
          USP & Distinctions
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Table structure - transposed so companies are rows and USP/DISTINCTION are columns */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '200px repeat(2, 1fr)',
            width: '100%',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden'
          }}
        >
          {/* Header row with field names */}
          <Box
            sx={{
              gridColumn: '1 / 2',
              gridRow: '1 / 2',
              p: 1.5,
              borderColor: 'primary.main',
              bgcolor: 'background.neutral'
            }}
          ></Box>

          {fields.map((field, fieldIndex) => (
            <Box
              key={field}
              sx={{
                gridColumn: `${fieldIndex + 2} / ${fieldIndex + 3}`,
                gridRow: '1 / 2',
                p: 1.5,
                borderColor: 'primary.main',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.neutral'
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center'
                }}
              >
                {field}
              </Typography>
            </Box>
          ))}

          {/* Competitor rows */}
          {competitors.map((competitor, compIndex) => (
            <React.Fragment key={competitor.url}>
              {/* Competitor name */}
              <Box
                sx={{
                  gridColumn: '1 / 2',
                  gridRow: `${compIndex + (hasCompanyUsp ? 2 : 1)} / ${compIndex + (hasCompanyUsp ? 3 : 2)}`,
                  p: 1.5,
                  borderBottom: compIndex < competitors.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  bgcolor: compIndex % 2 === 0 ? 'background.paper' : 'background.neutral',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    bgcolor: getCompetitorColor(compIndex),
                    borderRadius: '50%',
                    mr: 1.5
                  }}
                />
                <Favicon
                  company={
                    competitor.overview_result?.find((item) => Object.keys(item)[0] === 'Service Name')?.['Service Name'] || competitor.url
                  }
                  size={20}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, ml: 1 }}>
                  {competitor.overview_result?.find((item) => Object.keys(item)[0] === 'Service Name')?.['Service Name'] || competitor.url}
                </Typography>
              </Box>

              {/* Values for each field */}
              {fields.map((field, fieldIndex) => {
                const usp = competitor.usp;
                const items = usp && usp[field] && Array.isArray(usp[field]) ? usp[field] : [];
                const competitorColor = getCompetitorColor(compIndex);

                return (
                  <Box
                    key={`${competitor.url}-${field}`}
                    sx={{
                      gridColumn: `${fieldIndex + 2} / ${fieldIndex + 3}`,
                      gridRow: `${compIndex + (hasCompanyUsp ? 2 : 1)} / ${compIndex + (hasCompanyUsp ? 3 : 2)}`,
                      p: 1.5,
                      borderBottom: compIndex < competitors.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: compIndex % 2 === 0 ? 'background.paper' : 'background.neutral'
                    }}
                  >
                    {items.length > 0 ? (
                      <List dense disablePadding sx={{ mt: -1, mb: -1, ml: 0.5 }}>
                        {items.map((item, itemIndex) => (
                          <ListItem
                            key={itemIndex}
                            disableGutters
                            sx={{
                              py: 0.5,
                              display: 'flex',
                              alignItems: 'flex-start'
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: '3px',
                                width: '3px',
                                alignSelf: 'stretch',
                                backgroundColor: competitorColor,
                                mr: 1,
                                borderRadius: '1px'
                              }}
                            />
                            <Typography variant="body1">{item}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        N/A
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const FieldBasedComparison = ({
  analysisData,
  companyData,
  showSecondaryMenu: externalShowSecondaryMenu,
  menuMinimized: externalMenuMinimized,
  isPaywalled = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const contentRef = useRef(null);
  const [activeField, setActiveField] = useState(null);
  const [internalShowSecondaryMenu, setInternalShowSecondaryMenu] = useState(true);
  const [internalMenuMinimized, setInternalMenuMinimized] = useState(false);
  const dispatch = useDispatch();
  const trialWasActivated = useSelector((state) => state.proAccountReducer?.trial_was_activated);
  const userCountry = useSelector((state) => state.proAccountReducer?.country);
  const restrictedCountries = ['IN', 'VN', 'CN', 'BD', 'PK', 'ID'];
  const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);

  // Use the externally provided showSecondaryMenu if provided, otherwise use internal state
  const showSecondaryMenu = externalShowSecondaryMenu !== undefined ? externalShowSecondaryMenu : internalShowSecondaryMenu;
  // Use the externally provided menuMinimized if provided, otherwise use internal state
  const menuMinimized = externalMenuMinimized !== undefined ? externalMenuMinimized : internalMenuMinimized;

  // Show secondary menu only when we're in the field analysis view
  useEffect(() => {
    // Skip if external control is being used
    if (externalShowSecondaryMenu !== undefined) return;

    // Always show the secondary menu
    setInternalShowSecondaryMenu(true);

    return () => {
      setInternalShowSecondaryMenu(false);
    };
  }, [externalShowSecondaryMenu]);

  // Suppress click-driven state changes when there is an active text selection inside this section
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const hasSelection = () => {
      try {
        const sel = window.getSelection();
        return !!(sel && !sel.isCollapsed && String(sel.toString() || '').trim().length > 0);
      } catch (_) {
        return false;
      }
    };
    const onClickCapture = (e) => {
      if (hasSelection()) {
        try {
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        } catch (_) {
          /* ignore */
        }
        try {
          e.stopPropagation();
        } catch (_) {
          /* ignore */
        }
        try {
          e.preventDefault();
        } catch (_) {
          /* ignore */
        }
      }
    };
    el.addEventListener('click', onClickCapture, true);
    return () => {
      try {
        el.removeEventListener('click', onClickCapture, true);
      } catch (_) {
        /* ignore */
      }
    };
  }, []);

  // Make sure we have valid data
  if (!analysisData || !Array.isArray(analysisData) || analysisData.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No competitor analysis data available.</Typography>
      </Box>
    );
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuMinimizeToggle = (minimized) => {
    setInternalMenuMinimized(minimized);
  };

  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'competitor-field-analysis',
      afterCapture: handleClose
    });
  };

  const handleTrialClick = () => {
    const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);
    dispatch(
      openModal({
        title: 'Unlock Competitors Analysis',
        subtitle: 'Upgrade to Pro to view all insights across competitors.',
        trial: trialAllowed
      })
    );
  };

  const Gate = ({ children, cta }) => {
    const contentBlurRef = useRef(null);
    const overlayBlurRef = useRef(null);
    useAntiTamperBlur(contentBlurRef, overlayBlurRef, { blurPx: 5 });
    if (!isPaywalled) return children;
    return (
      <Box sx={{ position: 'relative' }}>
        <Box ref={contentBlurRef} sx={{ filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.85 }}>
          {children}
        </Box>
        <Paper
          ref={overlayBlurRef}
          elevation={3}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 3,
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(255,255,255,0.6)'
          }}
        >
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 6,
                p: { xs: 2, md: 3 },
                width: '92%',
                maxWidth: 720,
                textAlign: 'left'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {cta?.title || 'Unlock full report'}
              </Typography>
              {Array.isArray(cta?.bullets) && cta.bullets.length > 0 && (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {cta.bullets.map((b, i) => (
                    <Grid key={i} item xs={12} sm={6}>
                      <Typography variant="body2">• {b}</Typography>
                    </Grid>
                  ))}
                </Grid>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleTrialClick}
                  component={motion.button}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{ px: 3, py: 1.25, background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)', boxShadow: 'none' }}
                >
                  {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Cancel anytime
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Paper>
      </Box>
    );
  };

  // Filter competitors for the overview section
  const overviewCompetitors = analysisData.filter((competitor) => {
    return competitor.overview_result && competitor.overview_result.length > 0;
  });

  // Determine if pricing has any content at all (company or any competitor)
  const hasPricingContent = Boolean(companyData?.pricing_analysis) || analysisData.some((c) => Boolean(c?.pricing_analysis));

  // Generate proper field categories with correct IDs
  const fieldCategories = {};

  // Add Side-by-Side Analysis fields
  if (FIELD_CATEGORIES['Side-by-Side Analysis']) {
    fieldCategories['Side-by-Side Analysis'] = {
      ...FIELD_CATEGORIES['Side-by-Side Analysis'],
      sectionId: 'section-sbs-analysis'
    };
  }

  // Add USP fields
  if (FIELD_CATEGORIES['USP & Distinctions']) {
    fieldCategories['USP & Distinctions'] = {
      ...FIELD_CATEGORIES['USP & Distinctions'],
      sectionId: 'section-usp',
      fields: FIELD_CATEGORIES['USP & Distinctions'].fields.map((field) => ({
        name: field,
        id: `usp-${field.toLowerCase().replace(/\s+/g, '-')}`
      }))
    };
  }

  // Add Overview fields
  if (FIELD_CATEGORIES['Overview']) {
    fieldCategories['Overview'] = {
      ...FIELD_CATEGORIES['Overview'],
      sectionId: 'section-overview-fields',
      fields: FIELD_CATEGORIES['Overview'].fields.map((field) => ({
        name: field,
        id: `field-${field.toLowerCase().replace(/\s+/g, '_')}`
      }))
    };
  }

  // Add Market Trends fields
  if (FIELD_CATEGORIES['Market Trends']) {
    fieldCategories['Market Trends'] = {
      ...FIELD_CATEGORIES['Market Trends'],
      sectionId: 'section-marketTrends',
      fields: FIELD_CATEGORIES['Market Trends'].fields.map((field) => ({
        name: field,
        id: `market-${field.toLowerCase().replace(/\s+/g, '-')}`
      }))
    };
  }

  // Add Pricing Analysis fields
  if (FIELD_CATEGORIES['Pricing Analysis']) {
    fieldCategories['Pricing Analysis'] = {
      ...FIELD_CATEGORIES['Pricing Analysis'],
      sectionId: 'section-pricing',
      fields: FIELD_CATEGORIES['Pricing Analysis'].fields.map((field) => ({
        name: field,
        id: `pricing-${field.toLowerCase().replace(/\s+/g, '-')}`
      }))
    };
  }

  // Handle field selection
  const handleFieldSelect = ({ field, category }) => {
    const fieldName = field.name || field;
    setActiveField(fieldName);

    // Different ID formats for different sections
    let fieldId;
    if (category === 'USP & Distinctions') {
      // Handle USP & Distinctions as a single menu point
      fieldId = 'section-usp';
    } else if (category === 'Overview') {
      // Overview fields in OverviewTabsView just use the original field name
      fieldId = `field-${fieldName}`;

      // First make sure we're in "All Fields" view
      const allFieldsButton =
        document.querySelector('[data-field-index="all"]') || document.querySelector('.MuiGrid-item > div:first-of-type'); // Try to find All Fields button

      if (allFieldsButton) {
        allFieldsButton.click(); // Show all fields first
      }
    } else if (category === 'Market Trends') {
      fieldId = `market-${fieldName.toLowerCase().replace(/\s+/g, '-')}`;
    } else if (category === 'Pricing Analysis') {
      fieldId = `pricing-${fieldName.toLowerCase().replace(/\s+/g, '-')}`;
    } else if (category === 'Side-by-Side Analysis') {
      // For Side-by-Side Analysis, use the field's id directly
      fieldId = field.id;
    } else {
      fieldId = field.id || `field-${fieldName.toLowerCase().replace(/\s+/g, '_')}`;
    }

    // First try to scroll to the specific field
    const fieldElement = document.getElementById(fieldId);

    if (fieldElement) {
      setTimeout(() => {
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100); // Small delay to allow All Fields view to render
    } else {
      // If field element not found, scroll to the section
      const sectionId = fieldCategories[category]?.sectionId;
      if (sectionId) {
        const sectionElement = document.getElementById(sectionId);
        if (sectionElement) {
          sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  return (
    <Box
      sx={{
        mb: 4,
        ml: showSecondaryMenu ? (menuMinimized ? 0 : '220px') : 0,
        transition: 'margin-left 0.3s ease',
        pt: 2
      }}
      ref={contentRef}
    >
      {/* Second layer menu for the report that sticks next to the main menu */}
      {showSecondaryMenu && (
        <ReportSecondaryMenu
          categories={fieldCategories}
          activeField={activeField}
          onFieldSelect={handleFieldSelect}
          visible={showSecondaryMenu}
          onMinimizeToggle={handleMenuMinimizeToggle}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
        >
          Field-Based Comparison
        </Typography>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ height: 28, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
          aria-label="download options"
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                border: 1,
                borderColor: 'secondary.light',
                borderRadius: 1
              }
            }
          }}
        >
          <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
        </Menu>
      </Box>

      <Grid container spacing={3}>
        {/* Main content area - adjust width based on secondary menu visibility */}
        <Grid item xs={12} md={12} lg={12}>
          {/* Special USP & Distinctions component first */}
          {/* <Grid item xs={12}>
            {(() => {
              const uspCompetitors = analysisData.filter((competitor) => {
                return competitor.usp !== null;
              });

              if (uspCompetitors.length === 0) return null;

              return <UspTableCard competitors={uspCompetitors} companyData={companyData} />;
            })()}
          </Grid> */}

          {/* Overview section with individual cards for each field */}
          {overviewCompetitors.length > 0 && (
            <Grid item xs={12} id="section-overview-fields">
              <OverviewTabsView
                competitors={overviewCompetitors}
                companyData={companyData}
                isPaywalled={isPaywalled}
                handleTrialClick={handleTrialClick}
                trialAllowed={trialAllowed}
              />
            </Grid>
          )}

          {/* Market Trends Component */}
          <Grid item xs={12} id="section-marketTrends">
            <Gate
              cta={{
                title: 'Unlock Market Trends & Benchmarks',
                bullets: ['Trends overview', 'Industry benchmarks', 'Actionable insights']
              }}
            >
              <CompaniesMarketTrends
                competitors={analysisData
                  .filter((competitor) => competitor.url !== companyData?.url)
                  .map((competitor) => {
                    const serviceName = competitor.overview_result?.find((item) => 'Service Name' in item)?.['Service Name'];
                    return {
                      ...competitor,
                      company_name: serviceName || competitor.url,
                      analysis_data: {
                        market_trends: competitor.market_trends
                      }
                    };
                  })}
                companyData={{
                  ...companyData,
                  service_name: companyData?.overview_result?.find((item) => 'Service Name' in item)?.['Service Name'],
                  company_name: companyData?.overview_result?.find((item) => 'Service Name' in item)?.['Service Name'],
                  analysis_data: {
                    market_trends: companyData?.market_trends
                  }
                }}
              />
            </Gate>
          </Grid>

          {/* Pricing Analysis Component (only if content exists) */}
          {hasPricingContent && (
            <Grid item xs={12} id="section-pricing">
              <Gate
                cta={{
                  title: 'Unlock Pricing Analysis',
                  bullets: ['Pricing model & strategy', 'Conclusions & insights']
                }}
              >
                <CompaniesPricingAnalysis
                  competitors={analysisData
                    .filter((competitor) => competitor.url !== companyData?.url)
                    .map((competitor) => {
                      const serviceName = competitor.overview_result?.find((item) => 'Service Name' in item)?.['Service Name'];
                      return {
                        ...competitor,
                        company_name: serviceName || competitor.url,
                        analysis_data: {
                          pricing_analysis: competitor.pricing_analysis
                        }
                      };
                    })}
                  companyData={{
                    ...companyData,
                    service_name: companyData?.overview_result?.find((item) => 'Service Name' in item)?.['Service Name'],
                    company_name: companyData?.overview_result?.find((item) => 'Service Name' in item)?.['Service Name'],
                    analysis_data: {
                      pricing_analysis: companyData?.pricing_analysis
                    }
                  }}
                />
              </Gate>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default FieldBasedComparison;
